import React, { type FunctionComponent, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Button, Popover, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { useAppMessaging } from '../../../hooks/useAppMessaging';
import BreakoutRoomOptionsContainer from '../BreakoutRoomOptionsContainer';
import { StyledButtonContainer, StyledSubButtonGroup, StyledButtonGroup } from '../styles/styled-components';

interface BreakoutRoomRowProps {
    roomName: string;
    roomId: string;
}

const RenameRoom: FunctionComponent<BreakoutRoomRowProps> = ({ roomName, roomId }) => {
    const { t } = useTranslation('breakout_room');
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [newRoomName, setNewRoomName] = useState(null);
    const [nameInvalid, setNameInvalid] = useState(false);
    const { roomsOpen, roomState } = useContainer(BreakoutRoomOptionsContainer);
    const { sendMessage } = useAppMessaging();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleTextClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };

    const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    const handleTextChanged = (event) => {
        const newValue = event.target.value;

        const nameExists = roomState.findIndex((room) => room.name === newValue) >= 0;

        setNameInvalid(nameExists);

        if (!nameExists) {
            setNewRoomName(event.target.value);
        }
    };

    const onNewNameEntered = (event: React.MouseEvent<HTMLButtonElement>) => {
        handleClose(event);

        sendMessage({
            payload: { func: 'rename_breakout_room', room_id: roomId, name: newRoomName }
        });
    };

    const open = Boolean(anchorEl);

    const errorHelper = nameInvalid ? 'Error: name already exists' : '';

    return (
        <StyledButtonContainer>
            <StyledButtonGroup>
                <span>
                    <div>
                        <Button
                            aria-describedby={t('Rename')}
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={handleClick}
                            disabled={roomsOpen}
                            size="small"
                        >
                            {t('Rename')}
                        </Button>
                        <Popover
                            id={t('rename-popup')}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                        >
                            <TextField
                                onClick={handleTextClick}
                                onChange={handleTextChanged}
                                defaultValue={roomName}
                                error={nameInvalid}
                                helperText={errorHelper}
                            />
                            <StyledSubButtonGroup>
                                <Button
                                    aria-describedby={t('Rename')}
                                    variant="contained"
                                    onClick={onNewNameEntered}
                                    style={{ margin: '5px' }}
                                    disabled={nameInvalid}
                                >
                                    {t('Rename')}
                                </Button>
                                <Button
                                    aria-describedby={t('Cancel')}
                                    variant="outlined"
                                    onClick={handleClose}
                                    style={{ margin: '5px' }}
                                >
                                    {t('Cancel')}
                                </Button>
                            </StyledSubButtonGroup>
                        </Popover>
                    </div>
                </span>
            </StyledButtonGroup>
        </StyledButtonContainer>
    );
};

export default RenameRoom;
